import React, { useState } from 'react';
import {
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Link,
} from '@mui/material';
import {
  Keyboard as KeyboardIcon,
  Info as InfoIcon,
  Article as ArticleIcon,
  Feedback as FeedbackIcon,
  NewReleases as ReleaseIcon,
} from '@mui/icons-material';

interface HelpMenuProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
}

const APP_VERSION = '0.1.0';
const BUILD_DATE = new Date().toISOString().split('T')[0];

const shortcuts = [
  { keys: 'Ctrl + /', description: 'Suche öffnen' },
  { keys: 'Ctrl + D', description: 'Dashboard' },
  { keys: 'Ctrl + K', description: 'Kurse' },
  { keys: 'Ctrl + B', description: 'Badges' },
  { keys: 'Ctrl + ,', description: 'Einstellungen' },
  { keys: '?', description: 'Hilfe anzeigen' },
];

function HelpMenu({ anchorEl, open, onClose }: HelpMenuProps) {
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [releaseNotesOpen, setReleaseNotesOpen] = useState(false);

  const handleOpenShortcuts = () => {
    onClose();
    setShortcutsOpen(true);
  };

  const handleOpenAbout = () => {
    onClose();
    setAboutOpen(true);
  };

  const handleOpenReleaseNotes = () => {
    onClose();
    setReleaseNotesOpen(true);
  };

  return (
    <>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={onClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={handleOpenShortcuts}>
          <ListItemIcon><KeyboardIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Tastenkürzel</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleOpenReleaseNotes}>
          <ListItemIcon><ReleaseIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Release Notes</ListItemText>
        </MenuItem>
        <MenuItem component="a" href="https://docs.taegener.info" target="_blank">
          <ListItemIcon><ArticleIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Dokumentation</ListItemText>
        </MenuItem>
        <MenuItem component="a" href="mailto:support@taegener.info">
          <ListItemIcon><FeedbackIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Feedback</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleOpenAbout}>
          <ListItemIcon><InfoIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Über</ListItemText>
        </MenuItem>
      </Menu>

      {/* Shortcuts Dialog */}
      <Dialog open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Tastenkürzel</DialogTitle>
        <DialogContent>
          <Table size="small">
            <TableBody>
              {shortcuts.map((shortcut) => (
                <TableRow key={shortcut.keys}>
                  <TableCell sx={{ width: 150 }}>
                    <Box
                      component="kbd"
                      sx={{
                        px: 1,
                        py: 0.5,
                        bgcolor: 'action.selected',
                        borderRadius: 1,
                        fontFamily: 'monospace',
                        fontSize: '0.85em',
                      }}
                    >
                      {shortcut.keys}
                    </Box>
                  </TableCell>
                  <TableCell>{shortcut.description}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShortcutsOpen(false)}>Schließen</Button>
        </DialogActions>
      </Dialog>

      {/* About Dialog */}
      <Dialog open={aboutOpen} onClose={() => setAboutOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Über RITTEC Lernplattform</DialogTitle>
        <DialogContent>
          <Box textAlign="center" py={2}>
            <Typography variant="h4" color="primary" gutterBottom>
              RITTEC Learn
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Version {APP_VERSION}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Build: {BUILD_DATE}
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Typography variant="body2">
              Die interne Lernplattform für RITTER Technologie Mitarbeiter.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              © 2024 RITTER Technologie GmbH
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAboutOpen(false)}>Schließen</Button>
        </DialogActions>
      </Dialog>

      {/* Release Notes Dialog */}
      <Dialog open={releaseNotesOpen} onClose={() => setReleaseNotesOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Release Notes</DialogTitle>
        <DialogContent>
          <Box>
            <Typography variant="h6" gutterBottom>
              Version 0.1.0 - Erste Version
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {BUILD_DATE}
            </Typography>
            <Typography variant="body2" paragraph>
              Initiale Version der RITTEC Lernplattform mit folgenden Features:
            </Typography>
            <Box component="ul" sx={{ pl: 2 }}>
              <li>Dashboard mit Fortschrittsübersicht</li>
              <li>Kursübersicht und -verwaltung</li>
              <li>Gamification: Punkte, Badges, Streaks</li>
              <li>Zertifikate mit PDF-Download</li>
              <li>Rangliste</li>
              <li>Dark/Light Mode</li>
              <li>Keycloak SSO-Integration</li>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReleaseNotesOpen(false)}>Schließen</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default HelpMenu;
